import React, { useCallback } from 'react';
import { createUseStyles } from 'react-jss';

import { Theme } from '@app/theme';
import { DecayFunctionConfig } from '@app/api';
import { Input, Select, Toggle } from '@app/form';
import { FoldableOpenMore } from 'components';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginBottom: theme.marginBase * 4,
  },
  toggle: {
    marginBottom: theme.marginBase,
    marginTop: 0,
  },
  content: {
    marginTop: theme.marginBase,
    backgroundColor: theme.colors.white,
    padding: theme.marginBase,
    borderRadius: theme.borderRadius.std,
  },
  sameLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase,
  },
  link: {
    ...theme.fonts.caption,
    color: theme.colors.blueAccessible,
    marginTop: theme.marginBase,
    display: 'block',
    textDecoration: 'underline',
  },
}));

interface Props {
  label: string;
  name: string;
  value: DecayFunctionConfig;
  onChange: (name: string, value: DecayFunctionConfig) => any;
}

const types = ['gauss', 'exp', 'linear'];

export const DecayScoringFields = ({ label, name, value, onChange }: Props) => {
  const classes = useStyles();

  const onEnabledChange = useCallback(
    (checked: boolean) => {
      onChange(name, { ...value, enabled: checked });
    },
    [onChange, name, value],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(name, { ...value, [e.target.name]: Number(e.target.value) });
    },
    [onChange, name, value],
  );

  const onSelectChange = useCallback(
    (option: { value: string }, _: any, name?: string) => {
      if (!name) {
        return;
      }
      onChange(name, { ...value, [name]: option.value });
    },
    [onChange, name, value],
  );

  return (
    <div className={classes.container}>
      <Toggle
        label={label}
        checked={value.enabled}
        onChange={onEnabledChange}
        containerClass={classes.toggle}
      />
      <FoldableOpenMore label="Avancé">
        <div className={classes.content}>
          <Input
            label="Poid"
            name="weight"
            value={value.weight}
            onChange={onInputChange}
            small
            noMargin
            type="number"
            step={0.1}
            min={0}
          />

          <div className={classes.sameLine}>
            <Select
              label="Type"
              name="type"
              options={types}
              value={value.type}
              onChange={onSelectChange}
              smallSelect
              noMargin
            />
          </div>

          <div className={classes.sameLine}>
            <Input
              label="Origin"
              name="origin"
              value={value.origin}
              onChange={onInputChange}
              small
              type="number"
              noMargin
            />
            <Input
              label="Scale"
              name="scale"
              value={value.scale}
              onChange={onInputChange}
              small
              type="number"
              noMargin
            />
          </div>
          <Input
            label="Decay"
            name="decay"
            value={value.decay}
            onChange={onInputChange}
            small
            noMargin
            type="number"
            step={0.1}
            min={0}
            max={1}
          />

          <a
            className={classes.link}
            href="https://docs.google.com/spreadsheets/d/1fE_4CVAhFqcP_S1y6BIeSyWZ6BYjvFfCkJhnhDlS2C0/edit?usp=sharing"
            target="_blank"
            rel="noreferrer"
          >
            Jouer avec les valeurs
          </a>
        </div>
      </FoldableOpenMore>
    </div>
  );
};
